import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, CircularProgress, Alert, Tooltip, Chip, Stack,
} from '@mui/material';
import {
  GridOn as GridOnIcon, Download as DownloadIcon, Info as InfoIcon,
} from '@mui/icons-material';
import axios from '../api/axios';

const TABS = [
  { key: 'reachability', label: 'Доступность', grid: true,
    hint: 'Какое устройство может достучаться до какого (из подсетей/IP и связей). «Правдоподобно по сети», без учёта фаервола.' },
  { key: 'access', label: 'Доступы',
    hint: 'Кто/что имеет доступ к каким устройствам и приложениям (люди + аккаунты).' },
  { key: 'app-comms', label: 'Связь приложений',
    hint: 'Как приложения общаются: источник → приёмник, порт, протокол, и каким путём идёт по сети.' },
  { key: 'app-inventory', label: 'Что где стоит',
    hint: 'Какое ПО / приложение установлено на каком устройстве.' },
];

const REL_COLOR = {
  direct: '#16a34a',       // green — есть кабель
  same_subnet: '#2563eb',  // blue — одна подсеть
  routed: '#f59e0b',       // amber — через роутер
  none: '#e5e7eb',         // grey — нет
};
const REL_TEXT = { direct: 'прямая', same_subnet: 'одна подсеть', routed: 'через роутер', none: 'нет' };

function Matrices() {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({});       // key -> response
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const active = TABS[tab];

  const load = useCallback(async (key) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`/matrix/${key}`);
      setData((d) => ({ ...d, [key]: res.data }));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!data[active.key]) load(active.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const exportFile = async (fmt) => {
    try {
      const res = await axios.get(`/matrix/${active.key}`, { params: { format: fmt }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${active.key}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const d = data[active.key];

  const renderGrid = () => {
    if (!d || !d.device_ids) return null;
    if (d.device_ids.length === 0) return <Alert severity="info">Нет устройств для отображения.</Alert>;
    const ids = d.device_ids;
    const names = d.names || {};
    const cellsByDev = {};
    (d.rows || []).forEach((r) => { cellsByDev[r.device_id] = r.cells || {}; });
    return (
      <Box>
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
          {Object.entries(REL_TEXT).map(([k, t]) => (
            <Chip key={k} size="small" label={t}
              sx={{ bgcolor: REL_COLOR[k], color: k === 'none' ? 'text.primary' : '#fff' }} />
          ))}
        </Stack>
        <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
          <Table size="small" stickyHeader sx={{ '& td, & th': { p: 0.4 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', fontWeight: 'bold' }}>
                  Источник \ Приёмник
                </TableCell>
                {ids.map((id) => (
                  <TableCell key={id} align="center" sx={{ whiteSpace: 'nowrap', fontSize: 11 }}>
                    <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', maxHeight: 120 }}>{names[id]}</span>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ids.map((rowId) => (
                <TableRow key={rowId} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', fontWeight: 500, whiteSpace: 'nowrap', fontSize: 12 }}>
                    {names[rowId]}
                  </TableCell>
                  {ids.map((colId) => {
                    if (colId === rowId) return <TableCell key={colId} sx={{ bgcolor: '#f8fafc' }} />;
                    const kind = cellsByDev[rowId]?.[colId] || 'none';
                    return (
                      <Tooltip key={colId} title={`${names[rowId]} → ${names[colId]}: ${REL_TEXT[kind] || kind}`} arrow>
                        <TableCell align="center" sx={{ bgcolor: REL_COLOR[kind] || '#e5e7eb', width: 22, height: 22, cursor: 'default' }} />
                      </Tooltip>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderTable = () => {
    if (!d || !d.headers) return null;
    if (!d.rows || d.rows.length === 0) {
      return <Alert severity="info">Пока нет данных. Заполни их в соответствующих разделах — матрица соберётся автоматически.</Alert>;
    }
    return (
      <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {d.headers.map((h, i) => (
                <TableCell key={i} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {d.rows.map((row, ri) => (
              <TableRow key={ri} hover>
                {row.map((cell, ci) => (
                  <TableCell key={ci} sx={{ whiteSpace: 'pre-wrap' }}>{cell === null || cell === undefined ? '' : String(cell)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GridOnIcon color="primary" />
          <Typography variant="h4">Матрицы</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportFile('xlsx')}>Excel</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => exportFile('csv')}>CSV</Button>
        </Stack>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TABS.map((t) => <Tab key={t.key} label={t.label} />)}
        </Tabs>
      </Paper>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>{active.hint}</Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        active.grid ? renderGrid() : renderTable()
      )}
    </Box>
  );
}

export default Matrices;
